import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Database, FileStack, Plug, Layers } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const evaluations = [
  { icon: Database, label: "Banco de dados", text: "Leitura estruturada dos dados que o sistema já armazena." },
  { icon: FileStack, label: "Arquivos e exportações", text: "Relatórios, planilhas e arquivos gerados pela operação." },
  { icon: Plug, label: "Integrações existentes", text: "Conectores, importadores e pontos de entrada já disponíveis." },
  { icon: Layers, label: "Interfaces estruturadas", text: "Interfaces previsíveis e, quando necessário, outras formas seguras de automação." },
];

export const LegacySystems = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section
      id="legacy"
      ref={ref}
      className="relative py-32 md:py-44 border-t border-white/5 overflow-hidden"
    >
      <motion.img
        style={{ y: bgY }}
        src="https://images.pexels.com/photos/8319695/pexels-photo-8319695.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
        alt="Estrutura geométrica"
        className="absolute inset-0 w-full h-[124%] object-cover opacity-20"
      />
      <div className="absolute inset-0 bg-[#050505]/85" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="max-w-[68ch]">
          <Reveal>
            <Overline>Diferencial</Overline>
            <h2 className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter leading-[0.98] mt-6">
              Seu sistema não possui API?
              <br />
              <span className="text-neutral-500">Isso não encerra a conversa.</span>
            </h2>
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed mt-8 max-w-[60ch]">
              Isso não significa automaticamente que ele não pode participar de
              uma automação. Avaliamos banco de dados, arquivos, integrações
              existentes e outras interfaces antes de definir a solução.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-[1px] bg-white/10 border border-white/10 rounded-sm overflow-hidden">
          {evaluations.map((e, i) => {
            const Icon = e.icon;
            return (
              <Reveal key={i} delay={i * 0.07} className="bg-[#0a0a0a]">
                <div className="p-7 md:p-8 h-full">
                  <Icon className="h-6 w-6 text-white" strokeWidth={1.3} />
                  <h3 className="font-display font-bold text-lg mt-5">{e.label}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed mt-3">
                    {e.text}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal>
          <div className="mt-10 flex items-start gap-3 rounded-sm border border-white/10 bg-[#0a0a0a] p-5 max-w-[70ch]">
            <span className="mt-1 h-2 w-2 rounded-full bg-amber-500 shrink-0" />
            <p className="text-neutral-400 text-sm leading-relaxed">
              Não prometemos que qualquer sistema pode ser automatizado sem
              análise técnica prévia. A viabilidade é sempre avaliada caso a
              caso, com transparência.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
